import { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './city.module.css';
import axios from 'axios';
// import { set } from 'mongoose';
import Notification from '../../shared/Notification';


// ─────────────────────────────────────────────────────────────────────────────
// helpers
// ─────────────────────────────────────────────────────────────────────────────
function buildCityId(cityName, countryName) {
    return `${cityName}__${countryName || 'unknown'}`
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
}

/**
 * Free, no-API-key city search using Open-Meteo geocoding API.
 * Returns up to 10 results with city/state/country.
 */
async function searchCities(query) {
    if (!query || query.trim().length < 2) return [];
    try {
        const res = await fetch(
            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query.trim())}&count=10&language=en&format=json`
        );
        if (!res.ok) throw new Error('fetch failed');
        const data = await res.json();
        return (data.results || []).map(g => ({
            cityName:    g.name,
            stateName:   g.admin1 || '',
            countryName: g.country || '',
            population:  g.population || 0,
        }));
    } catch {
        return [];
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// City (main component)
// ─────────────────────────────────────────────────────────────────────────────
const GuestCity = () => {

    const [myCity,       setMyCity]       = useState(null);   // { cityId, cityName, stateName, countryName }
    const [searchTerm,   setSearchTerm]   = useState('');
    const [results,      setResults]      = useState([]);
    const [searching,    setSearching]    = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [error,        setError]        = useState('');
    const [success,      setSuccess]      = useState('');
    const [membersPanel, setMembersPanel] = useState(false);
    const [schoolsPanel, setSchoolsPanel] = useState(false);
    const [notification, setNotification] = useState('');

    const flash = useCallback((setter, msg, ms = 3000) => {
        setter(msg);
        setTimeout(() => setter(''), ms);
    }, []);

    // ── load current city on mount ───────────────────────────────────────────
    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get('/api/guest/city/get-city');
                setMyCity(data.city || null);
            } catch (err) {
                // console.error('load city:', err);
            }
        })();
    }, []);

    // ── debounced search (500 ms) ────────────────────────────────────────────
    useEffect(() => {
        if (!searchTerm || searchTerm.trim().length < 2) {
            setResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearching(true);
            const cities = await searchCities(searchTerm);
            setResults(cities);
            setSearching(false);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    // ── save city ────────────────────────────────────────────────────────────
    const handleSelectCity = useCallback(async (city) => {
        setSaving(true);
        setError('');
        try {
            const { data } = await axios.put(
                '/api/guest/city/update-city',
                { city });
            setMyCity(data.city);
            setSearchTerm('');
            setResults([]);
            flash(setSuccess, 'City saved!');
        } catch (err) {
            // console.error('save city:', err);
            flash(setError, err.response?.data?.message || 'Failed to save city.');
        } finally {
            setSaving(false);
        }
    }, [flash]);

    // ── manual entry — just use whatever was typed ───────────────────────────
    const handleManualEntry = useCallback(() => {
        if (!searchTerm.trim()) return;
        handleSelectCity({
            cityName:    searchTerm.trim(),
            stateName:   '',
            countryName: '',
        });
    }, [searchTerm, handleSelectCity]);

    // ── remove city ──────────────────────────────────────────────────────────
    const handleRemoveCity = useCallback(async () => {
        setSaving(true);
        try {
            await axios.delete('/api/guest/city/remove-city');
            setMyCity(null);
            flash(setSuccess, 'City removed.');
        } catch {
            flash(setError, 'Failed to remove city.');
        } finally {
            setSaving(false);
        }
    }, [flash]);

    const cityId = myCity ? buildCityId(myCity.cityName, myCity.countryName) : null;

    const handleCloseNotification = () => {
        setNotification('');
    }

    return (
        <div className={styles.container}>
            {notification && (
                        <Notification message={notification} onClose={handleCloseNotification} />
                    )}

            <div className={styles.header}>
                <h2>{myCity ? 'My City' : 'Set Your City'}</h2>
                {saving && <span className={styles.savingIndicator}>Saving…</span>}
            </div>

            {success && <div className={styles.success}>{success}</div>}
            {error   && <div className={styles.error}>{error}</div>}

            {/* ── current city card ── */}
            {myCity && (
                <CityCard
                    city={myCity}
                    saving={saving}
                    onViewMembers={() => setMembersPanel(true)}
                    onViewSchools={() => setSchoolsPanel(true)}
                    onRemove={() => {
                        // handleRemoveCity
                        setNotification('Please Signup to Remove the City');
                    }}
                    onChange={() => {
                        //  setMyCity(null); setSearchTerm(''); 
                        setNotification('Please Signup to Change the City');
                        }}
                />
            )}

            {/* ── search (shown when no city set) ── */}
            {!myCity && (
                <>
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Search your city, e.g. Kanpur, Mumbai, Delhi…"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            autoFocus
                        />
                        {searching && <span className={styles.searchingIndicator}>Searching…</span>}
                    </div>

                    {results.length > 0 && (
                        <div className={styles.resultsList}>
                            {results.map((city, i) => (
                                <div
                                    key={i}
                                    className={styles.resultItem}
                                    onClick={() => handleSelectCity(city)}
                                >
                                    <span className={styles.cityIcon}>📍</span>
                                    <div className={styles.cityInfo}>
                                        <span className={styles.cityName}>{city.cityName}</span>
                                        <span className={styles.cityMeta}>
                                            {[city.stateName, city.countryName].filter(Boolean).join(', ')}
                                        </span>
                                    </div>
                                    {city.population > 0 && (
                                        <span className={styles.population}>
                                            {city.population.toLocaleString()}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* no results — offer manual save */}
                    {searchTerm.trim().length >= 2 && !searching && results.length === 0 && (
                        <div className={styles.noResults}>
                            <div className={styles.noResultsIcon}>🌍</div>
                            <p>No results for "<strong>{searchTerm}</strong>"</p>
                            <button className={styles.manualBtn} onClick={handleManualEntry}>
                                Use "{searchTerm}" as my city
                            </button>
                        </div>
                    )}

                    {/* also show manual-save button below results */}
                    {searchTerm.trim().length >= 2 && !searching && results.length > 0 && (
                        <div className={styles.manualHint}>
                            Don't see your city?{' '}
                            <button className={styles.manualLink} onClick={handleManualEntry}>
                                Save "{searchTerm}" directly
                            </button>
                        </div>
                    )}

                    {!searchTerm && (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>🏙️</div>
                            <h3>Where are you based?</h3>
                            <p>Discover people at local schools and colleges in your city</p>
                        </div>
                    )}
                </>
            )}

            {/* ── panels ── */}
            {membersPanel && cityId && (
                <CityMembersPanel
                    // token={token}
                    cityId={cityId}
                    cityName={myCity.cityName}
                    onClose={() => setMembersPanel(false)}
                />
            )}

            {schoolsPanel && cityId && (
                <CitySchoolsPanel
                    // token={token}
                    cityId={cityId}
                    cityName={myCity.cityName}
                    onClose={() => setSchoolsPanel(false)}
                    onViewSchoolMembers={(schoolId) => {
                        setSchoolsPanel(false);
                        setMembersPanel({ schoolId });
                    }}
                />
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CityCard
// ─────────────────────────────────────────────────────────────────────────────
const CityCard = ({ city, saving, onViewMembers, onViewSchools, onRemove, onChange }) => (
    <div className={styles.cityCard}>
        <div className={styles.cityCardLeft}>
            <div className={styles.cityCardIcon}>📍</div>
            <div>
                <div className={styles.cityCardName}>{city.cityName}</div>
                {(city.stateName || city.countryName) && (
                    <div className={styles.cityCardMeta}>
                        {[city.stateName, city.countryName].filter(Boolean).join(', ')}
                    </div>
                )}
            </div>
        </div>
        <div className={styles.cityCardActions}>
            <button className={styles.actionBtn} onClick={onViewMembers}>👥 People</button>
            <button className={styles.actionBtn} onClick={onViewSchools}>🏫 Schools</button>
            {!saving && <>
                <button className={styles.actionBtn} onClick={onChange}>✏️ Change</button>
                <button className={`${styles.actionBtn} ${styles.removeBtn}`} onClick={onRemove}>✕</button>
            </>}
        </div>
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CityMembersPanel
// ─────────────────────────────────────────────────────────────────────────────
const CityMembersPanel = ({cityId, cityName, onClose }) => {
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(false);
    const [page,       setPage]       = useState(1);
    const [typeFilter, setTypeFilter] = useState('');

    const fetchMembers = useCallback(async (p = 1, t = typeFilter) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ page: p, limit: 20 });
            if (t) params.set('type', t);
            const { data: res } = await axios.get(
                `/api/guest/city/city-members/${cityId}?${params}`);
            setData(prev => p === 1
                ? res
                : { ...res, members: [...(prev?.members || []), ...res.members] }
            );
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [cityId, typeFilter]);

    useEffect(() => { fetchMembers(1); setPage(1); }, [typeFilter]);

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={`${styles.modalContent} ${styles.membersModal}`} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h3>👥 People in {cityName}</h3>
                        {data && <span className={styles.memberCount}>{data.total} member{data.total !== 1 ? 's' : ''}</span>}
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div className={styles.filterRow}>
                    <select className={styles.filterSelect} value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
                        <option value="">All institution types</option>
                        <option value="school">School</option>
                        <option value="college">College</option>
                        <option value="university">University</option>
                    </select>
                </div>

                {loading && !data && <div className={styles.loadingMsg}>Loading…</div>}
                {data && data.members.length === 0 && <p className={styles.emptyMembers}>No members found.</p>}

                {data && data.members.length > 0 && (
                    <>
                        <div className={styles.memberGrid}>
                            {data.members.map(m => (
                                <div key={m._id} onClick={() => window.open(`/guest/profile/${m.applicantId}`, '_blank')} className={styles.memberCard}>
                                    <div className={styles.memberAvatar}>
                                        {m.profilePicture
                                            ? <img src={m.profilePicture} alt={m.name} />
                                            : <span>{(m.name || '?')[0].toUpperCase()}</span>}
                                    </div>
                                    <div className={styles.memberName}>{m.name || 'Anonymous'}</div>
                                    {m.schools?.length > 0 && (
                                        <div className={styles.memberSchools}>
                                            {m.schools.slice(0, 2).map((s, i) => (
                                                <span key={i} className={styles.memberSchoolTag}>
                                                    {s.name}{s.class ? ` · ${s.class}` : ''}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        {data.page < data.pages && (
                            <button className={styles.loadMoreBtn} disabled={loading}
                                onClick={() => { const n = page + 1; setPage(n); fetchMembers(n); }}>
                                {loading ? 'Loading…' : 'Load more'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// CitySchoolsPanel
// ─────────────────────────────────────────────────────────────────────────────
const CitySchoolsPanel = ({ cityId, cityName, onClose, onViewSchoolMembers }) => {
    const [data,       setData]       = useState(null);
    const [loading,    setLoading]    = useState(false);
    const [typeFilter, setTypeFilter] = useState('');

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data: res } = await axios.get(
                    `/api/guest/city/city-schools/${cityId}`);
                setData(res);
            } catch (err) { setData(null); }
            finally  { setLoading(false); }
        })();
    }, [cityId]);

    const filtered = useMemo(() => {
        if (!data?.schools) return [];
        return typeFilter ? data.schools.filter(s => s.type === typeFilter) : data.schools;
    }, [data, typeFilter]);

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={`${styles.modalContent} ${styles.schoolsModal}`} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h3>🏫 Schools in {cityName}</h3>
                        {data && <span className={styles.memberCount}>{filtered.length} institution{filtered.length !== 1 ? 's' : ''}</span>}
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}>×</button>
                </div>

                <div className={styles.typeTabs}>
                    {['', 'school', 'college', 'university'].map(t => (
                        <button key={t}
                            className={`${styles.typeTab} ${typeFilter === t ? styles.typeTabActive : ''}`}
                            onClick={() => setTypeFilter(t)}>
                            {t === '' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                    ))}
                </div>

                {loading && <div className={styles.loadingMsg}>Loading…</div>}
                {!loading && filtered.length === 0 && <p className={styles.emptyMembers}>No institutions found.</p>}

                {!loading && filtered.length > 0 && (
                    <div className={styles.schoolsList}>
                        {filtered.map(school => (
                            <div key={school.schoolId} className={styles.schoolItem}>
                                <div className={styles.schoolItemLeft}>
                                    <div className={styles.schoolItemName}>{school.name}</div>
                                    <div className={styles.schoolItemMeta}>
                                        <span className={styles.schoolTypeBadge}>{school.type}</span>
                                    </div>
                                </div>
                                <div className={styles.schoolItemRight}>
                                    <span className={styles.schoolMemberCount}>{school.count} member{school.count !== 1 ? 's' : ''}</span>
                                    <button className={styles.viewBtn} onClick={() => onViewSchoolMembers(school.schoolId)}>View</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default GuestCity;